import { defineStore } from 'pinia'
import { authApi } from '@/api'
import { resolveRoleHome, resolveRoleSwitchHome } from '@/router/access'

// ─── 后端契约（与 com.royaltiger.hrms.common.security.SecurityUser 对齐） ───
// 后端 SecurityUser:
//   userId / username / nickname / empId
//   roles:  List<String>     (角色 code)
//   perms:  Set<String>      (权限点 code)
//   scopeType:   ALL / CENTER / FACTORY / DEPT / SELF / CUSTOM
//   scopeOrgIds: List<Long>  (已展开的可见组织 id；scopeType=ALL 时为 null)
export interface UserInfo {
  userId: number
  username: string
  nickname: string
  empId?: number
  /** 当前人员头像：员工档案优先，账号头像兼容回退。 */
  avatar?: string | null
  roles: string[]
  perms: string[]
  scopeType: string
  scopeOrgIds?: number[]
  scopeEmpIds?: number[]
  // V112: 多角色真切换
  effectiveRoleCode?: string
  primaryRoleCode?: string
  effectiveOrgId?: number | null
  // UAT-FB#96 (2026-06-17): 演示模式 — 当前会话由谁伪装而来 (null = 正常登录)
  impersonatedBy?: string | null
}

// V112: /auth/my-roles 响应
export interface MyRoleOrgRef { id: number; name: string }
export interface MyRoleOption {
  code: string
  name: string
  scopeType: string
  defaultOrgId?: number | null
  orgs: MyRoleOrgRef[]
}
export interface MyRolesResp {
  currentRole: string
  currentOrgId: number | null
  availableRoles: MyRoleOption[]
}

/** 后端 /auth/login 响应（外层 R<T> 已被 response interceptor 拆掉，这里是 data 字段） */
interface LoginRespData {
  token: string
  user: UserInfo
  /** V235: 首次登录强制改密标记. true = 前端必须弹强制改密对话框, 未改前后端 filter 拦所有非豁免 API. */
  mustChangePassword?: boolean
}

interface UserState {
  token: string
  user: UserInfo | null
  // V112 多角色真切换
  availableRoles: MyRoleOption[]
  /** V235: 强制改密态标记. login 时若后端返 mustChangePassword=true 置位, 改密成功后清零. */
  mustChangePassword: boolean
}

// ─── 管理类角色集合（用于 isAdmin 判断） ───────────────────────────
// 与后端 AuthDataService.canPreview 对齐：HR 链路 + 部门负责人视为管理员。
const ADMIN_ROLES = new Set(['SYSTEM_ADMIN', 'SUPER', 'SUPER_ADMIN'])

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: '',
    user: null,
    availableRoles: [],
    mustChangePassword: false,
  }),
  getters: {
    /** 是否已登录（仅看 token，避免页面刷新时短暂的 user==null 误判） */
    isLogin: (s): boolean => !!s.token,
    /** 是否管理类角色（HR / 部门负责人 / 超管） */
    isAdmin: (s): boolean => !!s.user?.roles?.some(r => ADMIN_ROLES.has(r)),
    /** 当前用户的角色 code 列表（无登录时为空数组） */
    roles: (s): string[] => s.user?.roles ?? [],
    /** V112: 当前生效角色 code (受 /auth/switch-role 影响). 单角色用户 = 唯一角色; 未登录返空串. */
    effectiveRole: (s): string => s.user?.effectiveRoleCode ?? s.user?.roles?.[0] ?? '',
    /** 是否处于角色切换视角 (effectiveRole ≠ 登录基础角色) — 员工账号基础角色固定为 EMP。 */
    isInSwitchedView: (s): boolean => {
      const eff = s.user?.effectiveRoleCode
      const primary = s.user?.primaryRoleCode ?? s.user?.roles?.[0]
      return !!eff && !!primary && eff !== primary
    },
    /** 当前用户的权限点 code 列表（无登录时为空数组） */
    perms: (s): string[] => s.user?.perms ?? [],
    /** 数据可见域类型；未登录返回 SELF 作为最严格回退 */
    scopeType: (s): string => s.user?.scopeType ?? 'SELF',
    /**
     * 函数式 getter，便于模板里写 `userStore.can('rec:view_all')`。
     * 注意：与 action 形式的 hasPermission(code) 等价，二者保留以兼容现有调用风格。
     */
    can: (s) => (perm: string): boolean => s.user?.perms?.includes(perm) ?? false,
  },
  actions: {
    /**
     * 用户名密码登录；成功后写入 token + user，axios 拦截器会在后续请求自动带上 Bearer token。
     */
    async login(username: string, password: string, otp?: string): Promise<UserInfo> {
      const data = (await authApi.login({ username, password, otp })) as LoginRespData
      this.token = data.token
      this.user = data.user
      // V235: 后端返 mustChangePassword=true 时置位, Login.vue 据此弹强制改密对话框
      this.mustChangePassword = !!data.mustChangePassword
      return data.user
    },

    /**
     * V235: 改密成功后清 mustChangePassword 标记 + 替换 token (强制改密态 = 无缝续会话).
     * 由 Login.vue 强制改密对话框调用; 用户后续主动改密走同一端点但无 mustChangePassword 需清.
     */
    applyChangedPassword(newToken: string): void {
      this.token = newToken
      this.mustChangePassword = false
    },

    /**
     * 拉取当前登录用户最新信息（强制刷新缓存）。需要已存在 token。
     */
    async fetchMe(): Promise<UserInfo> {
      const me = (await authApi.me()) as UserInfo
      this.user = me
      return me
    },

    /** 角色、权限或数据范围变更后，原子刷新当前会话的完整授权上下文。 */
    async refreshAuthorizationContext(): Promise<UserInfo> {
      const me = await this.fetchMe()
      await this.loadMyRoles(true)
      return me
    },

    /**
     * 返回当前登录用户：内存有就直接返；没有但持有 token，就调 /auth/me 拉一次；
     * 完全未登录返回 null。供路由守卫 / 页面初始化使用。
     */
    async getCurrentUser(): Promise<UserInfo | null> {
      if (this.user) return this.user
      if (!this.token) return null
      try {
        return await this.fetchMe()
      } catch {
        // 拉取失败（多半 token 过期），response interceptor 已经清登录态并跳登录页
        return null
      }
    },

    /**
     * 用户主动退出：先调后端使缓存失效（失败也无所谓），再清本地 token + user。
     * 不在这里跳路由 —— 跳路由由调用方（菜单按钮等）决定。
     * 注意：response interceptor 在 401 时不应调用此方法（会触发同步 API 回环），
     * 它应直接调用 clearAuth() 仅做本地清理。
     */
    async logout(): Promise<void> {
      if (this.token) {
        try { await authApi.logout() } catch { /* 即便后端失败也要前端清干净 */ }
      }
      this.clearAuth()
    },

    /**
     * 仅清本地登录态，不调用任何 API。给 response interceptor / 强制下线场景用。
     */
    clearAuth(): void {
      this.token = ''
      this.user = null
      this.availableRoles = []
      this.mustChangePassword = false
    },

    /**
     * V112: 拉当前用户所有可切角色 + 每个角色的可选 org.
     * 单角色用户 availableRoles.length === 1, 调用方应据此决定是否显示切换 UI.
     */
    async loadMyRoles(silent = false): Promise<MyRoleOption[]> {
      const r = (await authApi.myRoles({ silent })) as MyRolesResp
      this.availableRoles = r.availableRoles || []
      // 同步当前生效角色到 user 对象 (后端 /auth/me 已返了, 但 my-roles 是权威)
      if (this.user) {
        this.user.effectiveRoleCode = r.currentRole
        this.user.effectiveOrgId = r.currentOrgId
      }
      return this.availableRoles
    },

    /**
     * V112: 切换到指定角色 (+ 可选 orgId). 拿到新 token 后替换并刷新页面,
     * 让所有 store 重载, 路由守卫重走权限校验.
     */
    async switchRole(roleCode: string, orgId?: number, preferredHome?: string): Promise<void> {
      const r = (await authApi.switchRole(roleCode, orgId)) as {
        token: string; username: string; role: string; orgId: number | null
      }
      this.token = r.token
      // 新 token 生效后立即重新获取当前角色的权限与数据范围；失败则不带着半刷新状态跳页。
      await this.refreshAuthorizationContext()
      // 整页 replace 清空各业务 store 的旧角色缓存，菜单会按新 perms 重新计算。
      window.location.replace(resolveRoleSwitchHome(r.role, preferredHome))
    },

    /**
     * UAT-FB#96 (2026-06-17): 演示模式 — 伪装为指定员工账号 (admin / SYSTEM_ADMIN 才能调).
     * 后端重签 token, subject 换成目标 user. 前端拿到后跳页面, 全模块按目标员工的视角生效.
     *
     * <p>UAT-FB#104 (2026-06-18): 不再 reload 留在原 url, 而是按新身份角色跳默认主页.
     * 原因: 留在原 url (比如 admin 在 /org 选中集团) 切到 CENTER_HR 会因 scope 不匹配
     * 导致一堆 403/404. 按角色跳到该身份"应该看到"的页面更符合产品逻辑.
     */
    async switchToUser(userId: number): Promise<void> {
      const r = (await authApi.switchToUser(userId)) as {
        token: string; username: string; role: string; orgId: number | null
      }
      this.token = r.token
      // 关键: user / availableRoles 整个清掉, 否则 persist 把老 user 写回 localStorage, 回来仍是老身份
      // 跳转后路由守卫调 /auth/me 拿真实新 user (含 impersonatedBy 字段)
      this.user = null
      this.availableRoles = []
      // UAT-FB#104: 按新身份角色跳默认主页
      const home = resolveRoleHome(r.role)
      window.location.href = home
    },

    /**
     * 检查当前用户是否拥有指定权限点。未登录或无该权限点均返回 false。
     */
    hasPermission(code: string): boolean {
      return this.user?.perms?.includes(code) ?? false
    },

    /**
     * 检查当前用户是否拥有指定角色。未登录返回 false。
     */
    hasRole(code: string): boolean {
      return this.user?.effectiveRoleCode === code || this.user?.roles?.includes(code) === true
    },

    /**
     * Token 刷新：当前后端无 /auth/refresh 接口（JwtUtil 仅 issue，无 refresh 路径），
     * 故此方法是个占位 no-op：返回当前 token，调用方应假设 token 在过期前一直有效。
     * 若后续后端补 /auth/refresh，则在此实现真正刷新逻辑。
     */
    async refreshToken(): Promise<string> {
      // TODO[backend]: /auth/refresh 端点未实现；token 过期由 response interceptor 401 处理。
      return this.token
    },
  },
  persist: {
    key: 'royal-tiger-user',
    storage: localStorage,
  },
})
